import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { X, Plus, Trash2, SlidersHorizontal, List, Check } from 'lucide-react';
import { SectionType } from '../types';

interface GlobalAttributesManagerProps {
    isOpen: boolean;
    onClose: () => void;
}

const GlobalAttributesManager: React.FC<GlobalAttributesManagerProps> = ({ isOpen, onClose }) => {
    const { config, updateConfig, pages } = useApp();
    const globalAttrs = config.globalAttributes || [];
    
    const [newLabel, setNewLabel] = useState('');
    const [newOptionsStr, setNewOptionsStr] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    
    // Get all categories (PRODUCT_GRID sections)
    const allCategories = pages.flatMap(p => 
        p.sections.filter(s => s.type === SectionType.PRODUCT_GRID).map(s => ({
            id: s.id,
            title: s.title || 'Nienazwana kategoria',
            pageTitle: p.title
        }))
    );

    const toggleCategory = (categoryId: string) => {
        setSelectedCategories(prev => 
            prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
        );
    };

    const toggleCategoryForExisting = (attrKey: string, categoryId: string) => {
        const updatedAttrs = globalAttrs.map(attr => {
            if (attr.key === attrKey) {
                const currentCats = attr.categoryIds || [];
                const newCats = currentCats.includes(categoryId) 
                    ? currentCats.filter(id => id !== categoryId)
                    : [...currentCats, categoryId];
                return { ...attr, categoryIds: newCats };
            }
            return attr;
        });
        updateConfig({ globalAttributes: updatedAttrs });
    };

    const addGlobalAttr = () => {
        if (!newLabel.trim()) return;
        
        const key = newLabel.toLowerCase()
            .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
            .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
            .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
            .replace(/[^a-z0-9]+/g, '_');

        // Split by comma, trim, remove empty, and de-duplicate!
        const rawOptions = newOptionsStr.split(',').map(o => o.trim()).filter(Boolean);
        const options = Array.from(new Set(rawOptions));

        updateConfig({
            globalAttributes: [...globalAttrs, { key, label: newLabel, options, categoryIds: selectedCategories }]
        });
        
        setNewLabel('');
        setNewOptionsStr('');
        setSelectedCategories([]);
    };

    const removeGlobalAttr = (key: string) => {
        updateConfig({
            globalAttributes: globalAttrs.filter(a => a.key !== key)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <SlidersHorizontal size={20} /> Zarządzanie Globalnymi Cechami
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded"><X size={20}/></button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto">
                    <p className="text-xs text-gray-500">
                        Zdefiniuj cechy globalne (np. Rozmiar, Kolor) i przypisz je bezpośrednio do odpowiednich kategorii. 
                        Produkty w tych kategoriach automatycznie otrzymają wybrane cechy.
                    </p>
                    
                    <div className="bg-emerald-50 p-4 rounded border border-emerald-100 space-y-3">
                        <h4 className="text-xs font-bold text-emerald-800 uppercase">Dodaj cechę globalną</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <input 
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    placeholder="Nazwa cechy (np. Materiał, Typ)"
                                    value={newLabel}
                                    onChange={(e) => setNewLabel(e.target.value)}
                                />
                                <input 
                                    className="w-full border rounded px-3 py-2 text-sm"
                                    placeholder="Warianty po przecinku (np. M, L, XL)"
                                    value={newOptionsStr}
                                    onChange={(e) => setNewOptionsStr(e.target.value)}
                                />
                                <button onClick={addGlobalAttr} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                                    <Plus size={14} /> Zapisz Cechę
                                </button>
                            </div>
                            <div className="bg-white p-3 border rounded text-xs">
                                <span className="font-bold text-gray-600 mb-2 block">Przypisz do kategorii:</span>
                                {allCategories.length === 0 && <span className="text-gray-400">Brak dostępnych kategorii.</span>}
                                <div className="space-y-1 max-h-32 overflow-y-auto pr-2">
                                    {allCategories.map(cat => (
                                        <label key={cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                            <input 
                                                type="checkbox" 
                                                className="accent-emerald-600"
                                                checked={selectedCategories.includes(cat.id)}
                                                onChange={() => toggleCategory(cat.id)}
                                            />
                                            <span className="truncate">{cat.pageTitle} - {cat.title}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Skonfigurowane Cechy</h4>
                        <div className="bg-gray-50 rounded border divide-y">
                            {globalAttrs.length === 0 && <p className="p-4 text-center text-xs text-gray-400">Brak zdefiniowanych globalnych cech.</p>}
                            {globalAttrs.map(attr => (
                                <div key={attr.key} className="p-4 flex flex-col gap-3 group">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className="font-bold text-sm text-gray-800 block">{attr.label}</span>
                                            {attr.options && attr.options.length > 0 && (
                                                <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                                    <List size={10} /> {attr.options.join(', ')}
                                                </span>
                                            )}
                                        </div>
                                        <button onClick={() => removeGlobalAttr(attr.key)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Usuń cechę">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                    <div className="mt-2">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Włączona w kategoriach:</span>
                                        <div className="flex flex-wrap gap-2 text-[10px]">
                                            {allCategories.map(cat => {
                                                const isActive = attr.categoryIds?.includes(cat.id);
                                                return (
                                                    <button
                                                        key={cat.id}
                                                        onClick={() => toggleCategoryForExisting(attr.key, cat.id)}
                                                        className={`px-2 py-1 border rounded flex items-center gap-1 transition-colors ${isActive ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-100'}`}
                                                    >
                                                        {isActive && <Check size={10} />}
                                                        {cat.title}
                                                    </button>
                                                );
                                            })}
                                            {allCategories.length === 0 && <span className="text-gray-400">Brak stworzonych kategorii produktów w sklepie.</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobalAttributesManager;
